'use client';

import { forwardRef, useContext, useMemo } from 'react';

import { cx } from '@/styled-system/css';

import { Icon } from '../icon/icon';
import { ToneIcon } from '../tone-icon';
import { TagRootContext } from './context/tag-root.context';
import type { TagIconPropsInterface } from './types/tag-icon.types';
import type { StatusTone } from './types/tag-root.types';

/**
 * @since 10.0.0
 */
export const TagIcon = forwardRef<SVGSVGElement, TagIconPropsInterface>((props, forwardedRef) => {
  const { size, tone } = useContext(TagRootContext);
  const { children, css: cssProp, ...rest } = props;

  const cssOverrides = {
    color: 'inherit',
    ...cssProp,
  };

  // Check if tone is a status and force to show ToneIcon when true
  const isStatusTone = useMemo(
    () => ['critical', 'info', 'success', 'warning'].some((status) => status === tone),
    [tone],
  );

  // Don't show icon when tag is small
  if (size === 'sm') {
    return null;
  }

  // Design would like for us to "force" the icon type if the tone is a status.
  if (isStatusTone) {
    return (
      <ToneIcon
        ref={forwardedRef}
        size="sm"
        tone={tone as StatusTone}
        css={cssOverrides}
        {...rest}
      />
    );
  }

  return (
    <Icon
      data-c11n-component="Tag.Icon"
      data-c11n-version={__C11N_VERSION__}
      ref={forwardedRef}
      size="sm"
      css={cssOverrides}
      {...rest}
      className={cx('c11n_tag__icon', rest.className)}
    >
      {children}
    </Icon>
  );
});

TagIcon.displayName = 'Tag.Icon';
